import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../lib/apiError';
import { usersApi } from './usersApi';
import type { UserDetail } from './userTypes';

export interface UserProfileState {
  user: UserDetail | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

/**
 * Loads one account for the profile page.
 *
 * Sign-in history is NOT loaded here: it sits behind its own permission and carries its own
 * search, sort, and paging, so `useSignInActivity` owns it. A caller who may read the account
 * but not its history still gets a complete page.
 *
 * `id` may be undefined while the route resolves; nothing is requested in that case.
 */
export function useUserProfile(id: string | undefined): UserProfileState {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!id) {
      setUser(null);
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setUser(null);

    usersApi
      .get(id, controller.signal)
      .then(setUser)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the account.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { user, loading, error, reload };
}
